// Public certificate verification -- see ADR-0009.
//
// Deliberately reachable without signing in: a credential nobody outside the
// site can check is not worth issuing. Shows only what a verifier needs --
// name, credential, issue date -- and never anything that identifies the
// holder's account.

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import theme from '../../theme';
import PageHeader from '../../components/PageHeader';
import { verifyCertificate } from '../../lib/capstoneApi';

const CSS = `
  .vfy-form { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 30px; }
  .vfy-input {
    flex: 1 1 240px;
    padding: 11px 14px;
    background: ${theme.bgDark};
    border: 1px solid rgba(var(--ap-text-muted-rgb), 0.35);
    border-radius: 3px;
    color: ${theme.textPrimary};
    font-family: 'Josefin Sans', sans-serif;
    font-size: 15px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  .vfy-result {
    max-width: 620px;
    padding: 30px 34px;
    border-radius: 4px;
    background: ${theme.bgCard};
  }
  .vfy-result.valid { border: 1px solid rgba(var(--ap-success-rgb), 0.5); }
  .vfy-result.invalid { border: 1px solid rgba(var(--ap-error-rgb), 0.45); }
  .vfy-badge {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 11px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    margin: 0 0 16px;
  }
  .vfy-badge.valid { color: var(--ap-success); }
  .vfy-badge.invalid { color: var(--ap-error); }
  .vfy-name {
    font-family: 'Cormorant Garamond', serif;
    font-size: 31px;
    color: ${theme.textPrimary};
    margin: 0 0 6px;
    text-wrap: balance;
  }
  .vfy-cred { font-family: 'Cormorant Garamond', serif; font-size: 20px; color: ${theme.gold}; margin: 0 0 20px; }
  .vfy-rows { margin: 0; padding-top: 18px; border-top: 1px solid rgba(var(--ap-text-muted-rgb), 0.2); }
  .vfy-row { display: flex; justify-content: space-between; gap: 16px; padding: 6px 0; font-family: 'Josefin Sans', sans-serif; font-size: 13px; }
  .vfy-row span:first-child { color: ${theme.textMuted}; letter-spacing: 0.05em; }
  .vfy-row span:last-child { color: ${theme.textPrimary}; font-variant-numeric: tabular-nums; }
  .vfy-note { font-family: 'EB Garamond', serif; font-size: 15px; color: ${theme.textMuted}; line-height: 1.7; margin: 18px 0 0; max-width: 60ch; }
`;

function VerifyCertificatePage() {
  const { code: codeParam } = useParams();
  const navigate = useNavigate();
  const [code, setCode] = useState(codeParam || '');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Verify a Certificate - Austrian Process';
  }, []);

  useEffect(() => {
    if (!codeParam) { setResult(null); return; }
    let active = true;
    setLoading(true);
    setCode(codeParam);
    verifyCertificate(codeParam)
      .then((r) => active && setResult(r))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [codeParam]);

  function submit(e) {
    e.preventDefault();
    const trimmed = code.trim().toUpperCase();
    if (trimmed) navigate(`/verify/${trimmed}`);
  }

  return (
    <div className="page-container">
      <style>{CSS}</style>
      <PageHeader
        title="Verify a Certificate"
        description="Check a Diploma in Austrian Economics against the issuing record."
      />

      <form className="vfy-form" onSubmit={submit}>
        <input
          className="vfy-input"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="AP-XXXX-XXXX"
          aria-label="Certificate verification code"
        />
        <button type="submit" className="btn-primary">Verify</button>
      </form>

      {loading && <p style={{ color: theme.textMuted }}>Checking...</p>}

      {!loading && result && (
        result.valid ? (
          <div className="vfy-result valid">
            <p className="vfy-badge valid">&#10003; Verified - this certificate was issued by Austrian Process</p>
            <h2 className="vfy-name">{result.recipientName}</h2>
            <p className="vfy-cred">{result.credential}</p>
            <div className="vfy-rows">
              <div className="vfy-row"><span>Issued</span><span>{new Date(result.issuedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</span></div>
              <div className="vfy-row"><span>Code</span><span>{result.verificationCode}</span></div>
            </div>
            <p className="vfy-note">
              The holder completed a three-level curriculum, defended an applied research essay
              under human review, and passed a comprehensive examination at 75%.
            </p>
          </div>
        ) : (
          <div className="vfy-result invalid">
            <p className="vfy-badge invalid">&#10007; Not found</p>
            <p className="vfy-note" style={{ margin: 0 }}>
              No certificate matches that code. Check it for typos - codes look like
              <strong> AP-XXXX-XXXX</strong> and never contain the letters O or I, or the digits
              0 or 1.
            </p>
          </div>
        )
      )}
    </div>
  );
}

export default VerifyCertificatePage;
